import { BreakpointObserver } from '@angular/cdk/layout';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MatIconRegistry } from '@angular/material/icon';
import { MatSnackBar } from '@angular/material/snack-bar';
import { DomSanitizer } from '@angular/platform-browser';
import { Subject, timer } from 'rxjs';
import { concatMap, switchMap, takeUntil } from 'rxjs/operators';
import { DomoticaService } from 'src/app/services/domotica/domotica.service';
import { NavigatorService } from 'src/app/services/navigator.service';
import { TenantDTO } from 'src/app/shared/dto/domain/tenant';
import { OfficeDoorStatusDTO } from 'src/app/shared/dto/domotica/officeDoorStatus';
import { ConfirmationDialogComponent } from '../../shared/confirmation-dialog/confirmation-dialog.component';
import { OfficeDoorWidgetChooseCommandDialogComponent } from './choose-command-dialog/office-door-widget-choose-command-dialog.component';
import { Icon } from './office-door-widget.model';
import { OfficeDoorWidgetReadQrCodeDialogComponent } from './read-qrcode-dialog/office-door-widget-read-qrcode-dialog.component';

@Component({
  selector: "app-office-door-widget",
  templateUrl: "./office-door-widget.component.html",
  styleUrls: ["./office-door-widget.component.scss"],
})
export class OfficeDoorWidgetComponent implements OnInit, OnDestroy {
  private timer$ = new Subject<any>();
  private _showWidget: boolean = false;
  private refreshTime: number = 5000;

  tenant: TenantDTO;
  status: OfficeDoorStatusDTO;
  doorIcon: Icon;
  lockIcon: Icon;

  constructor(
    private domoticaService: DomoticaService,
    private navigatorService: NavigatorService,
    private confirmDialog: MatDialog,
    private iconRegistry: MatIconRegistry,
    private sanitizer: DomSanitizer,
    private snackBar: MatSnackBar,
    private breakpointObserver: BreakpointObserver
  ) {
    iconRegistry.addSvgIcon("door-warning",        sanitizer.bypassSecurityTrustResourceUrl("assets/img/icons/door-closed-error.svg"));
    iconRegistry.addSvgIcon("door-closed",         sanitizer.bypassSecurityTrustResourceUrl("assets/img/icons/door-closed-locked.svg"));
    iconRegistry.addSvgIcon("door-locked",         sanitizer.bypassSecurityTrustResourceUrl("assets/img/icons/door-closed-locked-key.svg"));
    iconRegistry.addSvgIcon("door-closed-unknown", sanitizer.bypassSecurityTrustResourceUrl("assets/img/icons/door-closed-unknown.svg"));
    iconRegistry.addSvgIcon("door-unlocked",       sanitizer.bypassSecurityTrustResourceUrl("assets/img/icons/door-closed-unlocked.svg"));
    iconRegistry.addSvgIcon("door-open",           sanitizer.bypassSecurityTrustResourceUrl("assets/img/icons/door-open.svg"));
    iconRegistry.addSvgIcon("door-unknown",        sanitizer.bypassSecurityTrustResourceUrl("assets/img/icons/door-unknown.svg"));
    
    this.setUnknownStatus();
  }

  ngOnInit() {
    this.navigatorService.tenant.subscribe((res: TenantDTO) => this.tenant = res);

    this.navigatorService.getCustomConfiguration().subscribe((c: any) => {
      if(c?.officeDoorStatusWidgetEnable != null) {
        this._showWidget = c.officeDoorStatusWidgetEnable;
      }
      if(c?.officeDoorStatusWidgetRefreshTime != null) {
        this.refreshTime = c.officeDoorStatusWidgetRefreshTime;
      }

      if(this._showWidget) {
        this.pollStatus();
      }
    });
  }

  get showWidget() {
    return this._showWidget;
  }

  get isMobile(): boolean {
    return this.breakpointObserver.isMatched('(max-width: 959px)');
  }

  hasCommands(): boolean {
    return this.status?.availableCommands?.length > 0;
  }

  doCommand() {
    if(this.hasCommands()) {
      if(this.isAdmin()) {
        this.execCommand(this.execManualCommand);
      } else {
		this.execCommand(this.execQrCodeCommand);
      }
    }
  }

  private isAdmin(): boolean {
    return this.navigatorService.isJappTenantAdmin();
  }

  private execCommand(callback: (command: string, _this: OfficeDoorWidgetComponent) => void): void {
    const commands = this.status?.availableCommands;
    if(commands?.length == 1) {
      callback(commands[0], this);
    } else if(commands?.length > 1) {
      const data = commands
        .map(c => this.mapCommandIcon(c))
        .sort((c1, c2) => c1.order - c2.order);
      this.confirmDialog.open(OfficeDoorWidgetChooseCommandDialogComponent, { data: data })
        .afterClosed()
        .subscribe(command => {
          if(command) {
            callback(command, this);
          } else {
            this.confirmDialog.closeAll();
          }
        });
    }
  }

  private execQrCodeCommand(command: string, _this: OfficeDoorWidgetComponent) {
    _this.confirmDialog.open(OfficeDoorWidgetReadQrCodeDialogComponent, { data: command })
      .afterClosed()
      .subscribe(ret => {
        _this.confirmDialog.closeAll();
        if(ret === 'COMMAND_IN_PROGRESS') {
          _this.snackBar.open("Un comando è già in esecuzione, riprova tra qualche secondo.", null, { duration: 3000 });
        } else if(ret) {
          _this.elaborateStatus(ret);
        } else {
          _this.snackBar.open("Attenzione! QR Code non valido.", null, { duration: 3000 });
        }
      });
  }

  private execManualCommand(command: string, _this: OfficeDoorWidgetComponent) {
    _this.confirm(`Sei sicuro di voler ${_this.mapCommand(command)} la serratura della porta?`)
      .subscribe(result => {
        if(result) {
          _this.domoticaService.officeDoorCommand(command)
            .pipe(concatMap(() => _this.domoticaService.officeDoorStatus(true)))
            .subscribe({
              next: (res: OfficeDoorStatusDTO) => _this.elaborateStatus(res),
              error: (err) => {
                const msg = err?.status === 409
                  ? "Un comando è già in esecuzione, riprova tra qualche secondo."
                  : "Errore nell'esecuzione del comando.";
                _this.snackBar.open(msg, null, { duration: 3000 });
              }
            });
        }
        _this.confirmDialog.closeAll();
      });
  }

  private confirm(message: string) {
    return this.confirmDialog.open(ConfirmationDialogComponent, { data: message }).afterClosed();
  }

  private pollStatus() {
    // starts immediately and poll every `refreshTime` milliseconds
    timer(0, this.refreshTime)
      .pipe(
        switchMap(() => this.domoticaService.officeDoorStatus()), // <-- map to another observable
        takeUntil(this.timer$) // <-- close the subscription when `timer$` emits
      )
      .subscribe({
        next: (res: OfficeDoorStatusDTO) => this.elaborateStatus(res),
        error: (error: any) => {
          console.log(error);
          this.setUnknownStatus();
        },
      });
  }

  private elaborateStatus(status: OfficeDoorStatusDTO): void {
    this.status = status;
    this.doorIcon = this.mapDoorIcon(status.doorStatus, status.lockStatus);
  }

  private setUnknownStatus(): void {
    this.elaborateStatus({
      doorStatus: null,
      lockStatus: null,
      availableCommands: []
    });
  }

  ngOnDestroy() {
    // close the polling anyway when the component is closed
    this.timer$.next();
  }

  private mapCommand(command: string): string {
    switch (command) {
      case "LOCK":
        return "chiudere";
      case "UNLOCK":
        return "aprire";
      default:
        return "[?]";
    }
  }

  private mapDoorIcon(doorStatus: string, lockStatus: string): Icon {
    switch (doorStatus) {
      case "CLOSE":
        switch (lockStatus) {
          case "LATCHBOLTLOCKED":
            return {
              code: "door-closed",
              colorClass: "closed",
              statusLabel: "Chiusa",
              secondLabel: "Chiusa",
            };
          case "LOCKED":
            return {
              code: "door-locked",
              colorClass: "locked",
              statusLabel: "Chiusa",
              secondLabel: "Chiusa con mandate",
            };
          case "UNLOCKING":
            return {
              code: "door-locked",
              colorClass: "locked",
              statusLabel: "Chiusa",
              secondLabel: "In apertura",
            };
          case "UNLOCKED":
            return {
              code: "door-unlocked",
              colorClass: "unlocked",
              statusLabel: "Chiusa",
              secondLabel: "Aperta",
            };
          case "LOCKING":
            return {
              code: "door-unlocked",
              colorClass: "unlocked",
              statusLabel: "Chiusa",
              secondLabel: "In chiusura",
            };
          case "JAMMED":
            return {
              code: "door-warning",
              colorClass: "warning",
              statusLabel: "Chiusa",
              secondLabel: "Inceppata",
            };
          default:
            return {
              code: "door-closed-unknown",
              colorClass: "closed-unknown",
              statusLabel: "Chiusa",
              secondLabel: lockStatus,
            };
        };
      case "TIMEOUTNOTCLOSE":
      case "OPEN":
        return {
          code: "door-open",
          colorClass: "open",
          statusLabel: "Porta: Aperta",
        };
      default:
        return {
          code: "door-unknown",
          colorClass: "unknown",
          statusLabel: "Porta: " + doorStatus,
        };
    }
  }

  private mapCommandIcon(command: string): Icon {
    switch (command) {
      case "LOCK":
        return {
          code: command,
          colorClass: command.toLowerCase(),
          statusLabel: "CHIUDERE",
          order: 2,
        };
      case "UNLOCK":
        return {
          code: command,
          colorClass: command.toLowerCase(),
          statusLabel: "APRIRE",
          order: 1,
        };
      default:
        return {
          code: "UNKNOWN",
          colorClass: "unknown",
          statusLabel: command,
          order: 100
        };
    }
  }
}
